'use client';

import type {
  OrderUnreadSummary,
  OrderUnreadSummaryEntry,
} from '@lib/data/order-consultations';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { deriveUnread, initialState, unreadReducer } from './reducer';

type UnreadContextValue = {
  readonly totalUnread: number;
  readonly visibleEntries: readonly OrderUnreadSummaryEntry[];
  readonly markRead: (orderId: string) => void;
  readonly rehydrate: (summary: OrderUnreadSummary) => void;
};

const UnreadContext = createContext<UnreadContextValue | null>(null);

type UnreadProviderProps = {
  readonly initialSummary: OrderUnreadSummary;
  readonly children: React.ReactNode;
};

export default function UnreadProvider({
  initialSummary,
  children,
}: UnreadProviderProps) {
  const [state, dispatch] = useReducer(
    unreadReducer,
    initialSummary,
    initialState,
  );

  const markRead = useCallback((orderId: string) => {
    dispatch({ type: 'mark_read', orderId });
  }, []);

  const rehydrate = useCallback((summary: OrderUnreadSummary) => {
    dispatch({ type: 'rehydrate', summary });
  }, []);

  const value = useMemo<UnreadContextValue>(() => {
    const derived = deriveUnread(state);
    return {
      totalUnread: derived.totalUnread,
      visibleEntries: derived.visibleEntries,
      markRead,
      rehydrate,
    };
  }, [state, markRead, rehydrate]);

  return (
    <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>
  );
}

export function useUnread(): UnreadContextValue {
  const ctx = useContext(UnreadContext);
  if (ctx === null) {
    // Provider isn't mounted on a given route (e.g. an embedded checkout).
    // Return a benign empty value so consumers don't have to null-check.
    return {
      totalUnread: 0,
      visibleEntries: [],
      markRead: () => undefined,
      rehydrate: () => undefined,
    };
  }
  return ctx;
}
